import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserPlus, CheckCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import { PasswordInput } from '@/components/PasswordInput';

const BENEFITS = ['benefit1', 'benefit2', 'benefit3', 'benefit4'];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useTranslation('register');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: '', password: '',
    first_name: '', last_name: '', company_name: ''
  });

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.first_name || !form.last_name) {
      toast.error(t('fillRequired'));
      return;
    }
    if (form.password.length < 6) {
      toast.error(t('passwordMinLength'));
      return;
    }
    setLoading(true);
    try {
      await register({
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
        company_name: form.company_name,
      });
      toast.success(t('registerSuccess'));
      navigate('/tableau-de-bord');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('registerError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="font-heading text-2xl font-bold text-neutral-900">{t('title')}</h1>
          <p className="text-sm text-neutral-500 mt-1">{t('subtitle')}</p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mb-6" data-testid="register-benefits">
          {BENEFITS.map(k => (
            <div key={k} className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#F25C05] shrink-0" />
              <span className="text-sm text-neutral-500">{t(k)}</span>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-neutral-200 p-6 shadow-sm space-y-5" data-testid="register-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">{t('firstName')} *</Label>
              <Input id="first_name" value={form.first_name} onChange={(e) => update('first_name', e.target.value)} placeholder={t('firstNamePlaceholder')} data-testid="register-firstname-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">{t('lastName')} *</Label>
              <Input id="last_name" value={form.last_name} onChange={(e) => update('last_name', e.target.value)} placeholder={t('lastNamePlaceholder')} data-testid="register-lastname-input" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t('email')} *</Label>
            <Input id="email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder={t('emailPlaceholder')} data-testid="register-email-input" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_name">{t('company')}</Label>
            <Input id="company_name" value={form.company_name} onChange={(e) => update('company_name', e.target.value)} placeholder={t('companyPlaceholder')} data-testid="register-company-input" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('password')} *</Label>
            <PasswordInput id="password" value={form.password} onChange={(e) => update('password', e.target.value)} placeholder={t('passwordPlaceholder')} data-testid="register-password-input" />
          </div>

          <Button type="submit" className="w-full bg-[#F25C05] hover:bg-[#d94f04] text-white h-11" disabled={loading} data-testid="register-submit-button">
            {loading ? t('creating') : <><UserPlus className="w-4 h-4 mr-2" /> {t('registerBtn')}</>}
          </Button>

          <p className="text-xs text-neutral-400 text-center leading-relaxed">
            {t('legalBefore')}<Link to="/conditions-utilisation" className="text-[#F25C05] hover:underline">{t('legalTerms')}</Link>{t('legalAnd')}<Link to="/politique-confidentialite" className="text-[#F25C05] hover:underline">{t('legalPrivacy')}</Link>.
          </p>
        </form>

        <p className="text-center text-sm text-neutral-500 mt-6">
          {t('alreadyAccount')}{' '}
          <Link to="/connexion" className="font-medium text-[#1F3F72] hover:underline" data-testid="register-login-link">
            {t('loginLink')}
          </Link>
        </p>
      </div>
    </div>
  );
}
